import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { registerOverlayDismiss } from '../../utils/modalNavigation';

interface GlassModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
}

let activeModalsCount = 0;

export const GlassModal: React.FC<GlassModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '520px'
}) => {
  useEffect(() => {
    if (!isOpen) return;

    activeModalsCount++;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('ios-modal-active');

    // Register with hardware/gesture back button listener
    const unregisterBackHandler = registerOverlayDismiss(onClose);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      unregisterBackHandler();
      window.removeEventListener('keydown', handleKeyDown);
      activeModalsCount = Math.max(0, activeModalsCount - 1);
      if (activeModalsCount === 0) {
        document.body.style.overflow = 'unset';
        document.body.classList.remove('ios-modal-active');
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalElement = (
    <div
      className="glass-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div 
        className="glass-modal-content" 
        style={{ maxWidth, pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* iOS Drag Handle */}
        <div
          style={{ paddingTop: '14px', paddingBottom: '4px', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          title="Dismiss Sheet"
        >
          <div className="ios-sheet-handle" style={{ marginBottom: title ? '8px' : '0' }} />
        </div>

        {title && (
          <div
            className="flex-between"
            style={{
              padding: '8px 24px 16px',
              borderBottom: 'var(--border-hairline)'
            }}
          >
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>
              {title}
            </h3>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="ios-tap-target"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(118, 118, 128, 0.24)',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all var(--duration-fast)'
              }}
              title="Close modal"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div className="glass-modal-body" style={{ padding: '16px 20px 24px' }}>
          {children}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalElement, document.body)
    : modalElement;
};

